import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  AlertTriangle,
  Bold,
  Check,
  Copy,
  Info,
  Italic,
  List,
  Loader2,
  Maximize2,
  Minimize2,
  Printer,
  Redo2,
  Save,
  Undo2,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { PRODUCT_NAME } from "@src/shared/brand";
import { Button } from "@src/shared/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@src/shared/components/ui/tooltip";
import { cn } from "@src/shared/lib/utils";
import { is409Error, isNetworkError } from "@src/shared/utils/http/errors";

import {
  EncounterNoteMapperError,
  mapTipTapJsonToEncounterNoteDocument,
} from "../../../../domain/encounter-note.mapper";
import type {
  UpdateEncounterNoteRequest,
  UpdateEncounterNoteResponse,
} from "../../../../domain/encounter-note.types";

export type VisitNoteEditorSaveStatus =
  | "idle"
  | "dirty"
  | "saving"
  | "saved"
  | "conflict"
  | "error";

export interface VisitNoteEditorProps {
  value?: string | null;
  noteRevision?: number;
  placeholder?: string;
  ariaLabel?: string;
  minHeightClassName?: string;
  onPrint?: () => void | Promise<void>;
  printLabel?: string;
  printDisabled?: boolean;
  isPrinting?: boolean;
  onSave?: (
    input: UpdateEncounterNoteRequest,
  ) => Promise<UpdateEncounterNoteResponse>;
  showLocalSaveNotice?: boolean;
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

/**
 * Markdown-significant characters the server escapes (with a leading `\`)
 * when a literal occurrence would otherwise be reinterpreted as emphasis,
 * heading, code, or list syntax. Matches `MARKDOWN_ESCAPE_PATTERN` in
 * `apps/server/src/features/encounters/domain/encounter-note-markdown.ts`,
 * plus the leading `-` list marker escape.
 */
const ESCAPED_MARKDOWN_CHAR_PATTERN = /\\([\\*_`#-])/g;
/** Private-use sentinels so escaped literals survive structural Markdown parsing. */
const ESCAPE_SENTINEL_START = "\uE000";
const ESCAPE_SENTINEL_END = "\uE001";
const ESCAPE_SENTINEL_PATTERN = /\uE000MD(\d+)\uE001/g;

/**
 * Replaces `\X` escape sequences with sentinel tokens so the structural
 * Markdown parsing below (headings, lists, emphasis) only ever matches real
 * syntax, never an escaped literal character. Restored via
 * `restoreEscapedMarkdown` once HTML has been generated.
 */
function protectEscapedMarkdown(value: string): string {
  return value.replace(
    ESCAPED_MARKDOWN_CHAR_PATTERN,
    (_match, char: string) =>
      `${ESCAPE_SENTINEL_START}MD${char.charCodeAt(0)}${ESCAPE_SENTINEL_END}`,
  );
}

function restoreEscapedMarkdown(html: string): string {
  return html.replace(ESCAPE_SENTINEL_PATTERN, (_match, code: string) =>
    String.fromCharCode(Number(code)),
  );
}

function noteTextToHtml(value: string): string {
  const lines = protectEscapedMarkdown(value).split(/\r?\n/);
  const html: string[] = [];
  let listItems: string[] = [];

  const flushList = () => {
    if (listItems.length === 0) return;
    html.push(`<ul>${listItems.join("")}</ul>`);
    listItems = [];
  };

  const inlineMarkdownToHtml = (text: string): string => {
    const escaped = escapeHtml(text);
    return escaped
      .replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>")
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>");
  };

  for (const line of lines) {
    const trimmed = line.trim();

    if (!trimmed) {
      flushList();
      continue;
    }

    if (trimmed === "---") {
      flushList();
      html.push("<hr>");
      continue;
    }

    const headingMatch = /^(#{1,3})\s+(.*)$/.exec(trimmed);
    if (headingMatch) {
      flushList();
      const level = headingMatch[1].length;
      html.push(
        `<h${level}>${inlineMarkdownToHtml(headingMatch[2])}</h${level}>`,
      );
      continue;
    }

    if (/^[-*]\s+/.test(trimmed)) {
      listItems.push(
        `<li>${inlineMarkdownToHtml(trimmed.replace(/^[-*]\s+/, ""))}</li>`,
      );
      continue;
    }

    flushList();
    html.push(`<p>${inlineMarkdownToHtml(trimmed)}</p>`);
  }

  flushList();
  return restoreEscapedMarkdown(html.join(""));
}

function htmlForValue(value: string | null | undefined): string {
  return value?.trim() ? noteTextToHtml(value) : "<p></p>";
}

const AUTOSAVE_MS = 800;

const LOCAL_SAVE_NOTICE = `Changes are saved in ${PRODUCT_NAME.singular} only. Copy the note into your EHR to update the visit note there.`;

function statusLabel(status: VisitNoteEditorSaveStatus): string | null {
  switch (status) {
    case "dirty":
      return "Saving soon…";
    case "saving":
      return "Saving…";
    case "saved":
      return `Saved in ${PRODUCT_NAME.singular}`;
    case "conflict":
      return "Note changed elsewhere";
    case "error":
      return "Unable to save";
    default:
      return null;
  }
}

function ToolbarButton({
  label,
  pressed,
  disabled,
  onClick,
  children,
}: {
  label: string;
  pressed?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn(
            "size-7 rounded-md text-muted-foreground hover:text-foreground",
            pressed && "bg-primary/10 text-primary hover:text-primary",
          )}
          aria-label={label}
          aria-pressed={pressed}
          disabled={disabled}
          onClick={onClick}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

export function VisitNoteEditor({
  value,
  noteRevision = 0,
  placeholder = "The generated note will appear here when it is ready.",
  ariaLabel = "Visit note editor",
  minHeightClassName = "min-h-[360px]",
  onPrint,
  printLabel = "Print note",
  printDisabled,
  isPrinting = false,
  onSave,
  showLocalSaveNotice = true,
}: VisitNoteEditorProps) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [hasContent, setHasContent] = useState(Boolean(value?.trim()));
  const [dirty, setDirty] = useState(false);
  const [saveStatus, setSaveStatus] =
    useState<VisitNoteEditorSaveStatus>("idle");
  const [isSaving, setIsSaving] = useState(false);
  const [revision, setRevision] = useState(noteRevision);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);

  const dirtyRef = useRef(false);
  const baselineHtmlRef = useRef<string | null>(null);
  const appliedValueRef = useRef(value);
  const revisionRef = useRef(noteRevision);
  const pendingEditorContentRef = useRef<string | null | undefined>(undefined);
  const autosaveTimerRef = useRef<number | null>(null);
  const handleSaveRef = useRef<() => Promise<void>>(async () => undefined);
  const scheduleAutosaveRef = useRef(() => undefined as void);
  const clearAutosaveTimerRef = useRef(() => undefined as void);
  const [prevExternalProps, setPrevExternalProps] = useState({
    value,
    noteRevision,
  });

  // Adjust mirrored editor state while rendering when the parent note changes.
  // TipTap content updates stay in an effect below — that API is imperative.
  if (
    !dirtyRef.current &&
    (value !== prevExternalProps.value ||
      noteRevision !== prevExternalProps.noteRevision)
  ) {
    setPrevExternalProps({ value, noteRevision });

    if (value !== appliedValueRef.current) {
      appliedValueRef.current = value;
      pendingEditorContentRef.current = value;
      dirtyRef.current = false;
      setDirty(false);
      setHasContent(Boolean(value?.trim()));
      setRevision(noteRevision);
      revisionRef.current = noteRevision;
      setSaveStatus("idle");
      setErrorDetail(null);
      setCopyError(null);
    } else {
      setRevision(noteRevision);
      revisionRef.current = noteRevision;
    }
  }

  const clearAutosaveTimer = useCallback(() => {
    if (autosaveTimerRef.current == null) return;
    window.clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = null;
  }, []);

  const scheduleAutosave = useCallback(() => {
    if (!onSave) return;
    clearAutosaveTimer();
    autosaveTimerRef.current = window.setTimeout(() => {
      autosaveTimerRef.current = null;
      void handleSaveRef.current();
    }, AUTOSAVE_MS);
  }, [clearAutosaveTimer, onSave]);

  useEffect(() => {
    clearAutosaveTimerRef.current = clearAutosaveTimer;
  }, [clearAutosaveTimer]);

  useEffect(() => {
    scheduleAutosaveRef.current = scheduleAutosave;
  }, [scheduleAutosave]);

  const syncDirty = useCallback((nextDirty: boolean) => {
    dirtyRef.current = nextDirty;
    setDirty(nextDirty);
  }, []);

  const editor = useEditor({
    extensions: [StarterKit],
    content: htmlForValue(value),
    immediatelyRender: false,
    onUpdate({ editor: updatedEditor }) {
      const text = updatedEditor.getText().trim();
      setHasContent(text.length > 0);

      const baseline = baselineHtmlRef.current;
      const nextDirty =
        baseline != null && updatedEditor.getHTML() !== baseline;
      syncDirty(nextDirty);
      if (nextDirty) {
        setSaveStatus("dirty");
        setErrorDetail(null);
        setCopyError(null);
        scheduleAutosaveRef.current();
        return;
      }
      clearAutosaveTimerRef.current();
      setSaveStatus("idle");
    },
    editorProps: {
      attributes: {
        "aria-label": ariaLabel,
        class: cn(
          minHeightClassName,
          "flex-1 overflow-y-auto px-4 py-3 text-[13px] leading-relaxed outline-none",
        ),
      },
      handleDOMEvents: {
        blur: () => {
          if (!dirtyRef.current) return false;
          clearAutosaveTimerRef.current();
          void handleSaveRef.current();
          return false;
        },
      },
    },
  });

  useEffect(() => {
    return () => clearAutosaveTimer();
  }, [clearAutosaveTimer]);

  useEffect(() => {
    revisionRef.current = revision;
  }, [revision]);

  useEffect(() => {
    if (!editor) return;
    if (baselineHtmlRef.current == null) {
      baselineHtmlRef.current = editor.getHTML();
    }
  }, [editor]);

  useEffect(() => {
    if (!editor || pendingEditorContentRef.current === undefined) return;
    const nextValue = pendingEditorContentRef.current;
    pendingEditorContentRef.current = undefined;
    editor.commands.setContent(htmlForValue(nextValue));
    baselineHtmlRef.current = editor.getHTML();
  }, [editor, value, noteRevision]);

  useEffect(() => {
    if (!dirty) return;

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const buildSaveInput = useCallback((): UpdateEncounterNoteRequest => {
    if (!editor) {
      throw new EncounterNoteMapperError("Editor is not ready");
    }
    return {
      document: mapTipTapJsonToEncounterNoteDocument(editor.getJSON()),
      expectedRevision: revisionRef.current,
    };
  }, [editor]);

  const applySuccessfulLocalSave = useCallback(
    (result: UpdateEncounterNoteResponse) => {
      appliedValueRef.current = result.note;
      setRevision(result.noteRevision);
      revisionRef.current = result.noteRevision;
      if (editor) {
        baselineHtmlRef.current = editor.getHTML();
      }
      syncDirty(false);
      setErrorDetail(null);
      setCopyError(null);
      setSaveStatus("saved");
    },
    [editor, syncDirty],
  );

  const handleSave = useCallback(async () => {
    if (!editor || !onSave || isSaving || !dirtyRef.current) return;

    clearAutosaveTimer();
    setIsSaving(true);
    setSaveStatus("saving");
    setErrorDetail(null);

    try {
      const input = buildSaveInput();
      const result = await onSave(input);
      applySuccessfulLocalSave(result);
    } catch (error) {
      if (is409Error(error)) {
        setSaveStatus("conflict");
        setErrorDetail(
          "This note was updated in another tab or session. Copy your changes, reload the visit, then re-apply them.",
        );
      } else if (error instanceof EncounterNoteMapperError) {
        setSaveStatus("error");
        setErrorDetail(error.message);
      } else if (isNetworkError(error)) {
        setSaveStatus("error");
        setErrorDetail(
          "No connection. Your edits are kept here — reconnect and save again.",
        );
      } else {
        setSaveStatus("error");
        setErrorDetail(
          "Something went wrong on our end. Your edits are kept here — try saving again.",
        );
      }
    } finally {
      setIsSaving(false);
    }
  }, [
    applySuccessfulLocalSave,
    buildSaveInput,
    clearAutosaveTimer,
    editor,
    isSaving,
    onSave,
  ]);

  useEffect(() => {
    handleSaveRef.current = handleSave;
  }, [handleSave]);

  useEffect(() => {
    if (!onSave) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (
        !(event.metaKey || event.ctrlKey) ||
        event.key.toLowerCase() !== "s"
      ) {
        return;
      }
      event.preventDefault();
      void handleSave();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleSave, onSave]);

  useEffect(() => {
    if (!expanded) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setExpanded(false);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [expanded]);

  const canCopy = hasContent;
  const canSave = Boolean(onSave) && dirty && !isSaving && hasContent;
  const liveStatus = statusLabel(saveStatus);

  const copyNote = async () => {
    if (!editor) return;

    try {
      await navigator.clipboard.writeText(
        editor.getText({ blockSeparator: "\n" }),
      );
      setCopyError(null);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopyError(
        "Copy failed — clipboard access was denied. Select the note text and copy manually.",
      );
    }
  };

  const printNote = () => {
    if (!editor) return;

    const frame = document.createElement("iframe");
    frame.style.cssText =
      "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
    document.body.appendChild(frame);
    const doc = frame.contentDocument;
    if (!doc) {
      frame.remove();
      return;
    }

    doc.open();
    doc.write(`<!doctype html><html><head><title>Visit note</title><style>
      body{font-family:system-ui,sans-serif;padding:24px;line-height:1.55;color:#15171a}
      p{margin:0 0 10px} ul{margin:0 0 12px;padding-left:1.4rem} hr{border:0;border-top:1px solid #dfe4e7;margin:16px 0}
    </style></head><body>${editor.getHTML()}</body></html>`);
    doc.close();
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
    window.setTimeout(() => frame.remove(), 500);
  };
  const handlePrint = () => {
    if (onPrint) {
      void onPrint();
      return;
    }

    printNote();
  };
  const effectivePrintLabel = isPrinting ? "Generating report" : printLabel;
  const isPrintDisabled = isPrinting || (printDisabled ?? !canCopy);

  const shell = (
    <div
      className={cn(
        "flex h-full min-h-0 flex-1 flex-col rounded-[10px] border border-border bg-background shadow-none",
        expanded &&
          "fixed inset-4 z-50 rounded-xl shadow-2xl md-stitch:inset-8",
      )}
    >
      <TooltipProvider delayDuration={200}>
        <div className="flex min-h-10 items-center justify-between gap-2 rounded-t-[10px] border-border border-b bg-surface px-2 py-1">
          <div className="flex min-w-0 items-center gap-1">
            <ToolbarButton
              label="Undo"
              disabled={!editor?.can().undo()}
              onClick={() => editor?.chain().focus().undo().run()}
            >
              <Undo2 className="size-3.5" aria-hidden="true" />
            </ToolbarButton>
            <ToolbarButton
              label="Redo"
              disabled={!editor?.can().redo()}
              onClick={() => editor?.chain().focus().redo().run()}
            >
              <Redo2 className="size-3.5" aria-hidden="true" />
            </ToolbarButton>
            <span className="mx-1 h-5 w-px bg-border" aria-hidden="true" />
            <ToolbarButton
              label="Bold"
              pressed={editor?.isActive("bold")}
              onClick={() => editor?.chain().focus().toggleBold().run()}
            >
              <Bold className="size-3.5" aria-hidden="true" />
            </ToolbarButton>
            <ToolbarButton
              label="Italic"
              pressed={editor?.isActive("italic")}
              onClick={() => editor?.chain().focus().toggleItalic().run()}
            >
              <Italic className="size-3.5" aria-hidden="true" />
            </ToolbarButton>
            <ToolbarButton
              label="Bulleted list"
              pressed={editor?.isActive("bulletList")}
              onClick={() => editor?.chain().focus().toggleBulletList().run()}
            >
              <List className="size-3.5" aria-hidden="true" />
            </ToolbarButton>
          </div>

          <div className="flex min-w-0 flex-1 items-center justify-end gap-2 px-1">
            {liveStatus ? (
              <span
                className={cn(
                  "truncate text-[11px] text-muted-foreground",
                  saveStatus === "conflict" && "text-destructive",
                  saveStatus === "error" && "text-destructive",
                  saveStatus === "saved" && "text-success",
                )}
                role="status"
                aria-live="polite"
                title={errorDetail ?? liveStatus}
              >
                {(saveStatus === "conflict" || saveStatus === "error") && (
                  <AlertTriangle
                    className="mr-1 inline size-3 align-text-bottom"
                    aria-hidden="true"
                  />
                )}
                {liveStatus}
              </span>
            ) : null}

            {onSave ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 shrink-0 gap-1.5 px-2 text-[12px] text-muted-foreground hover:text-foreground"
                aria-label="Save changes"
                disabled={!canSave}
                onClick={() => void handleSave()}
              >
                {isSaving && saveStatus === "saving" ? (
                  <Loader2
                    className="size-3.5 animate-spin"
                    aria-hidden="true"
                  />
                ) : (
                  <Save className="size-3.5" aria-hidden="true" />
                )}
                Save changes
              </Button>
            ) : null}

            <ToolbarButton
              label={copied ? "Copied" : "Copy note"}
              disabled={!canCopy}
              onClick={copyNote}
            >
              {copied ? (
                <Check className="size-3.5 text-success" aria-hidden="true" />
              ) : (
                <Copy className="size-3.5" aria-hidden="true" />
              )}
            </ToolbarButton>
            <ToolbarButton
              label={effectivePrintLabel}
              disabled={isPrintDisabled}
              onClick={handlePrint}
            >
              {isPrinting ? (
                <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
              ) : (
                <Printer className="size-3.5" aria-hidden="true" />
              )}
            </ToolbarButton>
            <ToolbarButton
              label={expanded ? "Exit full screen" : "Expand note"}
              onClick={() => setExpanded((current) => !current)}
            >
              {expanded ? (
                <Minimize2 className="size-3.5" aria-hidden="true" />
              ) : (
                <Maximize2 className="size-3.5" aria-hidden="true" />
              )}
            </ToolbarButton>
          </div>
        </div>
      </TooltipProvider>

      {onSave && showLocalSaveNotice ? (
        <div
          className="flex items-start gap-2 border-border border-b bg-muted/40 px-3 py-1.5 text-[11px] text-muted-foreground"
          role="status"
        >
          <Info
            className="mt-0.5 size-3.5 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
          <span>{LOCAL_SAVE_NOTICE}</span>
        </div>
      ) : null}

      {copyError ||
      ((saveStatus === "conflict" || saveStatus === "error") && errorDetail) ? (
        <div
          className="border-border border-b bg-destructive/5 px-3 py-1.5 text-[11px] text-destructive"
          role="alert"
        >
          {copyError ?? errorDetail}
        </div>
      ) : null}

      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        {!hasContent ? (
          <p
            className="pointer-events-none absolute inset-x-0 top-0 px-4 py-3 text-[13px] text-muted-foreground leading-relaxed"
            aria-hidden="true"
          >
            {placeholder}
          </p>
        ) : null}
        <EditorContent
          editor={editor}
          className="flex min-h-0 flex-1 flex-col overflow-hidden [&_.ProseMirror_hr]:my-4 [&_.ProseMirror_hr]:border-border [&_.ProseMirror_p]:mb-2 [&_.ProseMirror_p]:mt-0 [&_.ProseMirror_strong]:font-semibold [&_.ProseMirror_ul]:mb-3 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-5"
        />
      </div>
    </div>
  );

  if (!expanded) return shell;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm"
        aria-hidden="true"
      />
      {shell}
    </>
  );
}
